import { cva } from "class-variance-authority";
import { type ChangeEvent, useEffect, useRef } from "react";
import { mergeRefs } from "../../utils";
import { TextareaProps } from "./types";

const textareaContainerVariants = cva(
  "box-border w-full relative flex border overflow-hidden border-neutral-100 hover:border-neutral-200 hover:outline hover:outline-[2px] hover:outline-neutral-200 focus-within:outline focus-within:outline-[2px] focus-within:outline-primary-500 focus-within:hover:outline-primary-500 rounded-xl",
  {
    variants: {
      invalid: {
        true: "outline outline-[2px] border-danger-500 outline-danger-500 hover:border-danger-500 hover:outline-danger-500 focus-within:border-danger-500 focus-within:outline-danger-500 focus-within:hover:border-danger-500 focus-within:hover:outline-danger-500",
      },
      disabled: {
        true: "pointer-events-none text-neutral-200 bg-neutral-100 border-neutral-200",
        false: "bg-white",
      },
    },
  },
);

const textareaFieldVariants = cva(
  "flex w-full outline-none bg-transparent border-0 font-normal text-sm text-neutral-900 placeholder:text-neutral-500/50 focus:outline-none px-4 py-3",
  {
    variants: {
      disabled: {
        true: "text-neutral-500 cursor-not-allowed",
      },
      autosize: {
        true: "min-h-0",
      },
    },
  },
);

export const Textarea = ({
  ref,
  invalid,
  autosize,
  textareaClassName,
  className,
  disabled,
  value,
  onChange,
  ...textareaProps
}: TextareaProps) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = mergeRefs(internalRef, ref);

  const rootClass = textareaContainerVariants({
    invalid,
    disabled: !!disabled,
    className,
  });

  const textareaClass = textareaFieldVariants({
    disabled: !!disabled,
    autosize,
    className: textareaClassName,
  });

  useEffect(() => {
    if (autosize && internalRef.current) {
      internalRef.current.style.height = "auto";
      internalRef.current.style.height =
        internalRef.current.scrollHeight + "px";
    }
  }, [autosize, value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    if (onChange) {
      onChange(e.currentTarget.value, e);
    }
  };

  return (
    <div className={rootClass} style={{ outlineOffset: "-1px" }}>
      <textarea
        {...textareaProps}
        ref={textareaRef}
        disabled={disabled}
        value={value}
        className={textareaClass}
        onChange={handleChange}
      />
    </div>
  );
};
